import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Wallboard, WallboardLayout } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useTenants } from '../../lib/api/hooks.js';
import { useSubroute } from '../../lib/subroute.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { CardHead, Empty, ErrorBanner, TableSkeleton } from '../../components/ui/Primitives.js';
import { BoardGrid, useWallboardData, singleQueueTemplate, allQueuesTemplate, TEMPLATES } from './widgets.js';
import { BoardEditor } from './BoardEditor.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Wallboards (owner, 26 September 2026): per queue unless a multi-queue board is chosen, built from
 * a list of widgets, saved to the account for anyone with queue access, and full screen on a TV.
 *
 *   /realtime/wallboard                  the account's boards, and a ready-made one per queue
 *   /realtime/wallboard/q/support        ready-made: everything about one queue
 *   /realtime/wallboard/all              ready-made: every queue
 *   /realtime/wallboard/<board>          a saved board;  …/<board>/edit  changing it
 *   /realtime/wallboard/new              choosing a template to start from
 *   /realtime/wallboard/new/<template>[/<queue>]   building one from it
 */
export function WallboardPage() {
  const tenantId = useSession((s) => s.activeTenantId);
  const can = useSession((s) => s.can);
  const mayBuild = can('queue:write');
  const route = useSubroute();
  const qc = useQueryClient();
  const [a, b] = route.rest;

  const saved = useQuery({
    queryKey: ['wallboards', tenantId],
    queryFn: () => api.get<{ data: Wallboard[] }>(`/v1/tenants/${tenantId}/wallboards`),
    enabled: !!tenantId,
  });
  const boardSlug = a && a !== 'q' && a !== 'all' && a !== 'new' ? a : null;
  const one = useQuery({
    queryKey: ['wallboard-saved', tenantId, boardSlug],
    queryFn: () => api.get<Wallboard>(`/v1/tenants/${tenantId}/wallboards/${encodeURIComponent(boardSlug!)}`),
    enabled: !!tenantId && !!boardSlug,
  });

  // The queues are needed for every view, the ready-made layouts included.
  const probe = useWallboardData(tenantId, null);
  const quickQueue = a === 'q' && b ? probe.queues.find((q) => q.slug === b) ?? null : null;
  const layout: WallboardLayout | null = useMemo(() => {
    if (a === 'all') return allQueuesTemplate();
    if (a === 'q') return quickQueue ? singleQueueTemplate(quickQueue.key) : null;
    if (a === 'new') {
      const tpl = TEMPLATES.find((t) => t.key === b);
      if (!tpl) return null;
      const q = tpl.queue ? (probe.queues.find((x) => x.slug === route.rest[2]) ?? probe.queues[0] ?? null) : null;
      return tpl.build(q?.key ?? null);
    }
    return one.data?.layout ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, route.rest[2], quickQueue?.key, one.data, probe.queues.length]);
  const title = a === 'all' ? 'All queues' : a === 'q' ? quickQueue?.name ?? 'Queue' : one.data?.name ?? '';

  const save = useMutation({
    mutationFn: ({ name, layout: l }: { name: string; layout: WallboardLayout }) => boardSlug
      ? api.put<{ slug: string }>(`/v1/tenants/${tenantId}/wallboards/${encodeURIComponent(boardSlug)}`, { name, layout: l })
      : api.post<{ slug: string }>(`/v1/tenants/${tenantId}/wallboards`, { name, layout: l }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['wallboards', tenantId] });
      void qc.invalidateQueries({ queryKey: ['wallboard-saved', tenantId] });
      route.go([r.slug]);
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/v1/tenants/${tenantId}/wallboards/${encodeURIComponent(boardSlug!)}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['wallboards', tenantId] }); route.go([]); },
  });

  if (!tenantId) return <AccountPicker />;

  // ---------------------------------------------------------- choosing a template
  if (a === 'new' && !b && mayBuild) {
    return <TemplateGallery queues={probe.queues.map((q) => ({ slug: q.slug, name: q.name }))}
      onPick={(tpl, q) => route.go(['new', tpl, ...(q ? [q] : [])])} onCancel={() => route.go([])} />;
  }

  // --------------------------------------------------------------- editing
  if ((a === 'new' || (boardSlug && b === 'edit')) && mayBuild) {
    if (!layout) return <TableSkeleton rows={3} cols={4} />;
    return (
      <>
        <h1 className="arc-h1" style={{ marginBottom: 14 }}>{a === 'new' ? 'New wallboard' : `Edit ${title}`}</h1>
        <BoardEditorWithData tenantId={tenantId} initialName={a === 'new' ? '' : title} initial={layout}
          saving={save.isPending} error={save.error}
          onSave={(name, l) => save.mutate({ name, layout: l })}
          onCancel={() => route.go(boardSlug ? [boardSlug] : [])}
          onDelete={boardSlug ? () => { void confirmAction({ title: `Delete the ${title} board?`, body: 'Screens showing it will stop.', action: 'Delete', danger: true }).then((ok) => { if (ok) remove.mutate(); }); } : undefined} />
      </>
    );
  }

  // ------------------------------------------------------------------ a board
  if (a) {
    if (one.error) return <ErrorBanner error={one.error} />;
    if (a === 'q' && probe.queues.length > 0 && !quickQueue) return <Empty icon="queue" title="No queue by that name" hint="Choose one from the wallboards list." />;
    if (!layout) return <TableSkeleton rows={3} cols={4} />;
    return <BoardScreen tenantId={tenantId} title={title} layout={layout}
      onEdit={boardSlug && mayBuild ? () => route.go([boardSlug, 'edit']) : undefined}
      onSaveAs={!boardSlug && mayBuild ? () => route.go(a === 'all' ? ['new', 'all-queues'] : ['new', 'everything', b ?? '']) : undefined}
      onBack={() => route.go([])} />;
  }

  // ------------------------------------------------------------------- the list
  const boards = saved.data?.data ?? [];
  return (
    <>
      <div className="arc-row" style={{ gap: 10, marginBottom: 14 }}>
        <h1 className="arc-h1" style={{ margin: 0, flex: 1 }}>Wallboards</h1>
        {mayBuild && <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => route.go(['new'])}><Icon name="plus" size={13} /> New board</button>}
      </div>
      {saved.error && <ErrorBanner error={saved.error} />}
      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Your boards" sub="saved to the account; anyone with queue access can open them" />
        {saved.data === undefined ? <div className="arc-card-body">…</div> : boards.length === 0 ? (
          <div className="arc-card-body arc-muted" style={{ fontSize: 13 }}>
            None yet. {mayBuild ? 'Build one from a list of widgets with New board, or open a ready-made one below.' : 'Your admin can build boards; the ready-made ones are below.'}
          </div>
        ) : (
          <div className="arc-table-wrap"><table className="arc-table">
            <thead><tr><th>Board</th><th>Widgets</th><th>Changed</th><th /></tr></thead>
            <tbody>{boards.map((x) => (
              <tr key={x.id} style={{ cursor: 'pointer' }} onClick={() => route.go([x.slug])}>
                <td style={{ fontWeight: 550 }}>{x.name}</td>
                <td className="arc-num">{x.layout.widgets.length}</td>
                <td className="arc-subtle">{new Date(x.updated_at).toLocaleDateString()}{x.updated_by_name ? ` by ${x.updated_by_name}` : ''}</td>
                <td className="arc-td-right">{mayBuild && (
                  <RowAction kind="edit" label={`Edit ${x.name}`} onClick={() => route.go([x.slug, 'edit'])} />)}</td>
              </tr>))}
            </tbody>
          </table></div>
        )}
      </div>
      <div className="arc-card">
        <CardHead title="Ready-made" sub="one queue with everything about it, or every queue at once" />
        <div className="arc-card-body arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {probe.queues.map((q) => <button key={q.key} type="button" className="arc-btn arc-btn-sm" onClick={() => route.go(['q', q.slug])}>{q.name}</button>)}
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => route.go(['all'])}>All queues</button>
          {probe.queues.length === 0 && <span className="arc-subtle" style={{ fontSize: 13 }}>No queues yet.</span>}
        </div>
      </div>
    </>
  );
}

/** The editor, with live figures drawn in the widgets as they are placed. */
function BoardEditorWithData(props: Omit<Parameters<typeof BoardEditor>[0], 'ctx'> & { tenantId: string }) {
  const data = useWallboardData(props.tenantId, props.initial);
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setClock(Date.now()), 1_000); return () => clearInterval(t); }, []);
  const campaigns = useBoardCampaigns(props.tenantId);
  return <BoardEditor {...props} ctx={{ queues: data.queues, detailBy: data.detailBy, dialBy: data.dialBy, campaigns, now: clock + data.skewMs, big: 1, tz: data.tz }} />;
}

/** One board on the screen, with full screen for the TV. */
function BoardScreen({ tenantId, title, layout, onEdit, onSaveAs, onBack }: {
  tenantId: string; title: string; layout: WallboardLayout;
  onEdit?: (() => void) | undefined; onSaveAs?: (() => void) | undefined; onBack: () => void;
}) {
  const data = useWallboardData(tenantId, layout);
  const boardCampaigns = useBoardCampaigns(tenantId);
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setClock(Date.now()), 1_000); return () => clearInterval(t); }, []);
  const now = clock + data.skewMs;

  const ref = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  const big = full ? 1.4 : 1;
  const clockText = new Date(now).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', ...(data.tz ? { timeZone: data.tz } : {}) });

  return (
    <div ref={ref} style={{ background: 'var(--arc-bg)', padding: full ? 28 : 0, minHeight: full ? '100vh' : undefined, overflow: 'auto' }}>
      <div className="arc-row" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {!full && <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-btn-icon" aria-label="All wallboards" title="All wallboards" onClick={onBack}><Icon name="arrowLeft" size={14} /></button>}
        <h1 className="arc-h1" style={{ margin: 0, fontSize: 24 * big }}>{title}</h1>
        <span className="arc-subtle arc-num" style={{ fontSize: 17 * big }}>{clockText}</span>
        <div className="arc-spacer" />
        {!full && onEdit && <button type="button" className="arc-btn arc-btn-sm" onClick={onEdit}><Icon name="edit" size={13} /> Edit board</button>}
        {!full && onSaveAs && <button type="button" className="arc-btn arc-btn-sm" onClick={onSaveAs} title="Save a copy of this board and change it">Customise</button>}
        <button type="button" className="arc-btn arc-btn-sm" title={full ? 'Leave full screen (Esc)' : 'Show on the whole screen'}
          onClick={() => { if (document.fullscreenElement) void document.exitFullscreen(); else void ref.current?.requestFullscreen?.(); }}>
          <Icon name="external" size={13} /> {full ? 'Exit full screen' : 'Full screen'}
        </button>
      </div>
      {!!data.error && <ErrorBanner error={data.error} />}
      <BoardGrid widgets={layout.widgets} ctx={{ queues: data.queues, detailBy: data.detailBy, dialBy: data.dialBy, campaigns: boardCampaigns, now, big, layout, tz: data.tz }} />
    </div>
  );
}

/** Staff looking across accounts: wallboards belong to one, so choose it first. */
function AccountPicker() {
  const tenants = useTenants({ limit: 200 });
  const setActive = useSession((s) => s.setActiveTenant);
  return (
    <div className="arc-card" style={{ maxWidth: 420 }}>
      <div className="arc-card-body arc-stack" style={{ gap: 10 }}>
        <strong>Whose wallboards?</strong>
        <select className="arc-select" defaultValue="" aria-label="Account" onChange={(e) => { if (e.target.value) setActive(e.target.value); }}>
          <option value="" disabled>Choose an account</option>
          {(tenants.data?.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
    </div>
  );
}

/** The boards to start from, and the queue a queue board is for. */
function TemplateGallery({ queues, onPick, onCancel }: {
  queues: Array<{ slug: string; name: string }>; onPick: (template: string, queue: string | null) => void; onCancel: () => void;
}) {
  const mayDial = useSession((s) => s.can)('campaign:read');
  const [queue, setQueue] = useState<string>(queues[0]?.slug ?? '');
  useEffect(() => { if (!queue && queues[0]) setQueue(queues[0].slug); }, [queues, queue]);
  return (
    <>
      <div className="arc-row" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <h1 className="arc-h1" style={{ margin: 0, flex: 1 }}>New wallboard</h1>
        {queues.length > 0 && (
          <label className="arc-row" style={{ gap: 8, fontSize: 13 }}>
            <span className="arc-subtle">For the queue</span>
            <select className="arc-select" style={{ width: 'auto' }} value={queue} onChange={(e) => setQueue(e.target.value)}>
              {queues.map((q) => <option key={q.slug} value={q.slug}>{q.name}</option>)}
            </select>
          </label>
        )}
        <button type="button" className="arc-btn arc-btn-sm" onClick={onCancel}>Cancel</button>
      </div>
      <p className="arc-muted" style={{ marginTop: 0, fontSize: 13 }}>Start from one of these; every widget can then be moved, resized, changed or removed.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
        {TEMPLATES.filter((t) => t.key !== 'dialler' || mayDial).map((t) => (
          <button key={t.key} type="button" className="arc-card" onClick={() => onPick(t.key, t.queue ? queue || null : null)}
            style={{ textAlign: 'left', padding: 16, cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.name}</div>
            <div className="arc-muted" style={{ fontSize: 12.5 }}>{t.hint}</div>
            {t.queue && queues.length > 0 && <div className="arc-subtle" style={{ fontSize: 12, marginTop: 8 }}>for {queues.find((q) => q.slug === queue)?.name}</div>}
          </button>
        ))}
      </div>
    </>
  );
}

/** Campaign names for dialler widgets, for those who may see campaigns (client-only, 0123). */
function useBoardCampaigns(tenantId: string): Array<{ id: string; name: string }> {
  const can = useSession((s) => s.can);
  const q = useQuery({
    queryKey: ['wallboard-campaigns', tenantId],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>(`/v1/tenants/${tenantId}/campaigns`),
    enabled: !!tenantId && can('campaign:read'),
    staleTime: 60_000,
  });
  return (q.data?.data ?? []).map((c) => ({ id: c.id, name: c.name }));
}
